import React, { useCallback } from "react";
import { RefreshCcw, Server } from "lucide-react";
import { AppConfig } from "../../utils/Constants";
import { useTheme } from "../../context/ThemeContext";

interface BackendUnavailableProps {
  message: string;
  onRetry: () => void;
  isRetrying: boolean;
}

const BackendUnavailable: React.FC<BackendUnavailableProps> = ({
  message,
  onRetry,
  isRetrying,
}) => {
  const { theme } = useTheme();
  const handleRetry = useCallback(() => {
    if (!isRetrying) {
      onRetry();
    }
  }, [onRetry, isRetrying]);

  return (
    <>
      <div className="absolute top-6 left-6 z-[9999] pointer-events-none">
        <img
          src={
            theme === "light"
              ? "https://cdn.forgeworks.ai/assets/forgeworks-logo-black.png"
              : AppConfig.LOGO_URL
          }
          alt="Forgeworks Logo"
          className="w-6 md:w-8 pointer-events-auto"
        />
      </div>
      <div className="flex flex-col items-center justify-center h-screen bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark font-sans px-6 relative overflow-hidden subtle-grid home-grid-subtle">

      <div className="text-center space-y-6 max-w-lg mx-auto relative z-[60] isolate">
        <div className="flex justify-center mb-6">
          <div className={`p-4 rounded-xl ${
            theme === "light"
              ? "bg-background-panel border border-divider/50"
              : "bg-secondary/10"
          }`}>
            <Server className={`w-8 h-8 ${
              theme === "light"
                ? "text-black"
                : "text-secondary"
            } animate-pulse`} />
          </div>
        </div>

        <h1 className="text-3xl md:text-4xl font-semibold text-black dark:text-text-primaryDark leading-tight tracking-tight">
          Rebooting... almost there, promise!
        </h1>

        <p className="text-base md:text-lg text-black dark:text-text-secondaryDark">
          {message ||
            "It looks like we're having trouble connecting to our servers. Please try again in a moment."}
        </p>

        <div className="flex justify-center relative z-20">
          <button
            type="button"
            onClick={handleRetry}
            disabled={isRetrying}
            className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-md text-base md:text-lg font-medium shadow-sm relative z-20 pointer-events-auto ${
              isRetrying
                ? "bg-gray-400 dark:bg-gray-600 cursor-not-allowed text-gray-300 dark:text-gray-400"
                : "bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90 cursor-pointer"
            } transition-all duration-200`}
          >
            {isRetrying ? (
              <div className="animate-spin w-5 h-5 border-2 border-current border-t-transparent rounded-full"></div>
            ) : (
              <>
                <RefreshCcw className="w-5 h-5" />
                Retry
              </>
            )}
          </button>
        </div>

        <p className="text-sm text-black dark:text-text-secondaryDark">
          Need assistance?{" "}
          <a
            href={AppConfig.LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-black dark:text-text-secondaryDark hover:underline"
          >
            Visit our LinkedIn for updates
          </a>
        </p>

        <footer className="text-xs text-black dark:text-text-secondaryDark mt-8">
          &copy; {new Date().getFullYear()} {AppConfig.PRODUCT_NAME} - All
          rights reserved.
        </footer>
      </div>
    </div>
    </>
  );
};

export default BackendUnavailable;
